import {
    AggregatorOneOfFilteredTypeModel,
    FilteredBettingOffers,
    FilteredEventInfos,
    FilteredMainMarkets,
    FilteredMarkets,
    FilteredMarketGroupsOverview,
    FilteredMarketsAndOutcomeRelation,
    FilteredMatchesWithEvents,
    FilteredOutcomes,
    FilteredStreamingProvider,
    FilteredEventStatistics,
    MatchListStoreModel,
} from './match-list-models';
import { filterAndPick, remapEntries, setEventsToMatches, setOutcomesAndBoToMarket } from './filter-items-from-records';
import omit from 'lodash/omit';
import without from 'lodash/without';
import {
    AggregatorBettingOfferModel,
    AggregatorDisciplineModel,
    AggregatorEventInfoModel,
    AggregatorMatchModel,
    AggregatorOneOfTypeModel,
    AggregatorOutcomeModel,
    ModelTypes,
} from '@om-fe/models';
import { RecordCreate, RecordDelete, RecordUpdate, UpdateActionPayload } from '@om-fe/api';
import { isBrowser } from '@om-fe/utils';

export default function applyUpdatesForMatchList(
    payload: UpdateActionPayload<AggregatorOneOfTypeModel>,
    state: MatchListStoreModel,
): MatchListStoreModel {
    const { journal, summary } = payload;
    const { created, deleted, updated } = summary;

    if (!created && !deleted && !updated) {
        return state;
    }
    let {
        filteredMatchesWithEvents,
        filteredMatchesIds,
        filteredEventInfos,
        filteredMainMarketsList,
        filteredMainMarkets,
        filteredMarketGroupsOverview,
        filteredMarketGroupsOverviewList,
        marketsWithOutcomesAndBettingOffers,
        filteredOutcomes,
        filteredBettingOffers,
        filteredMarketAndOutcomeRelation,
        filteredStreamingProviderEventRelation,
        filteredEventStatistics,
        filteredMainOutrightsMarketsList,
    } = state;

    let discipline: AggregatorDisciplineModel | undefined = state.discipline;

    if (created > 0) {
        const { creates } = journal;
        const { records } = payload;

        creates.forEach((createdElement: RecordCreate<AggregatorOneOfTypeModel>) => {
            const { id, entityType } = createdElement;

            if (entityType === ModelTypes.MATCH) {
                filteredMatchesWithEvents = {
                    ...filteredMatchesWithEvents,
                    [id]: {
                        ...pickElementFromRecords(records, id, entityType) as FilteredMatchesWithEvents,
                    },
                };
                
                if(!filteredMatchesIds.includes(id)){
                    filteredMatchesIds = [...filteredMatchesIds, id];
                }
            }

            if (entityType === ModelTypes.EVENT_INFO) {

                const updatedEventInfoElement: FilteredEventInfos = pickElementFromRecords(records, id, entityType) as FilteredEventInfos;
                const matchId: string = updatedEventInfoElement.eventId;

                filteredEventInfos = {
                    ...filteredEventInfos,
                    [id]: {
                        ...pickElementFromRecords(records, id, entityType) as FilteredEventInfos,
                    },
                };

                const newMatchWithEvents: FilteredMatchesWithEvents[] = setEventsToMatches(
                    [filteredMatchesWithEvents[matchId]],
                    filteredEventInfos,
                );

                filteredMatchesWithEvents = {
                    ...filteredMatchesWithEvents,
                    [matchId]: { ...newMatchWithEvents[0]! },
                };
            } else if (entityType === ModelTypes.STREAMING_PROVIDER_EVENT_RELATION) {
                filteredStreamingProviderEventRelation = {
                    ...filteredStreamingProviderEventRelation,
                    [id]: {
                        ...pickElementFromRecords(records, id, entityType) as FilteredStreamingProvider,
                    },
                };
            } else if (entityType === ModelTypes.EVENT_STATISTICS) {
                filteredEventStatistics = [
                    ...filteredEventStatistics,
                    id,
                ] as FilteredEventStatistics[];
            } else if (entityType === ModelTypes.MAIN_MARKET) {
                filteredMainMarketsList = [
                    ...filteredMainMarketsList,
                    id,
                ];
                filteredMainMarkets = {
                    ...filteredMainMarkets,
                    [id]: {
                        ...pickElementFromRecords(records, id, entityType) as FilteredMainMarkets,
                    },
                };
                filteredMainOutrightsMarketsList = [
                    ...filteredMainOutrightsMarketsList,
                    id,
                ];
            } else if (entityType === ModelTypes.MARKET_GROUP_OVERVIEW) {
                filteredMarketGroupsOverviewList = [
                    ...filteredMarketGroupsOverviewList,
                    id,
                ];
                filteredMarketGroupsOverview = {
                    ...filteredMarketGroupsOverview,
                    [id]: {
                        ...pickElementFromRecords(records, id, entityType) as FilteredMarketGroupsOverview,
                    },
                };
            } else if (entityType === ModelTypes.MARKET) {
                const newMarket: FilteredMarkets = pickElementFromRecords(records, id, entityType) as FilteredMarkets;

                const newFilteredBettingOffers: Record<string, FilteredBettingOffers> = filterAndPick({
                    filterPredicate: ['_type', ModelTypes.BETTING_OFFER],
                    data: records,
                }) as Record<string, FilteredBettingOffers>;

                const newFilteredOutcomes: Record<string, FilteredOutcomes> = filterAndPick({
                    filterPredicate: ['_type', ModelTypes.OUTCOME],
                    data: records,
                }) as Record<string, FilteredOutcomes>;

                const newFilteredMarketAndOutcomeRelationOutcomeId: Record<string, FilteredMarketsAndOutcomeRelation[]> =
                    filterAndPick({
                        filterPredicate: ['_type', ModelTypes.MARKET_OUTCOME_RELATION],
                        data: records,
                        groupDataBy: 'marketId',
                    }) as Record<string, FilteredMarketsAndOutcomeRelation[]>;

                marketsWithOutcomesAndBettingOffers = {
                    ...marketsWithOutcomesAndBettingOffers,
                    [id]: setOutcomesAndBoToMarket(
                        [newMarket],
                        newFilteredOutcomes,
                        newFilteredMarketAndOutcomeRelationOutcomeId,
                        newFilteredBettingOffers,
                    )[0]!,
                };
            } else if (entityType === ModelTypes.OUTCOME) {
                filteredOutcomes = {
                    ...filteredOutcomes,
                    [id]: {
                        ...pickElementFromRecords(records, id, entityType) as FilteredOutcomes,
                    },
                };
            } else if (entityType === ModelTypes.BETTING_OFFER) {
                filteredBettingOffers = {
                    ...filteredBettingOffers,
                    [id]: {
                        ...pickElementFromRecords(records, id, entityType) as FilteredBettingOffers,
                    },
                };
            } else if (entityType === ModelTypes.MARKET_OUTCOME_RELATION) {
                filteredMarketAndOutcomeRelation = {
                    ...filteredMarketAndOutcomeRelation,
                    [id]: {
                        ...pickElementFromRecords(records, id, entityType) as FilteredMarketsAndOutcomeRelation,
                    },
                };
            }
        });
    }

    if (deleted > 0) {
        const { deletes } = journal;
        
        deletes.forEach((deletedElement: RecordDelete) => {
            const { id, entityType } = deletedElement;

            if (entityType === ModelTypes.MATCH && filteredMatchesWithEvents.hasOwnProperty(id)) {
                filteredMatchesWithEvents = omit(filteredMatchesWithEvents, id);
                filteredMatchesIds = without(filteredMatchesIds, id);
            }

            if (entityType === ModelTypes.EVENT_INFO && filteredEventInfos.hasOwnProperty(id)) {
                const eventInfo: FilteredEventInfos = state.filteredEventInfos[id];
                const match: FilteredMatchesWithEvents = filteredMatchesWithEvents[eventInfo.eventId];

                if (match && match.currentEventPartId === id) {
                    filteredMatchesWithEvents = {
                        ...filteredMatchesWithEvents,
                        [eventInfo.eventId]: {
                            ...match,
                            currentEventPartId: undefined,
                        },
                    };
                }
                
                filteredEventInfos = omit(filteredEventInfos, id);
            }
            if (entityType === ModelTypes.MAIN_MARKET) {
                filteredMainMarketsList = filteredMainMarketsList
                    .filter((mainMarketId: string) => mainMarketId !== id);
                filteredMainMarkets = omit(filteredMainMarkets, id);
                filteredMainOutrightsMarketsList = filteredMainOutrightsMarketsList
                    .filter((mainMarketId: string) => mainMarketId !== id);
            }
            if (entityType === ModelTypes.MARKET && marketsWithOutcomesAndBettingOffers.hasOwnProperty(id)) {
                marketsWithOutcomesAndBettingOffers = omit(marketsWithOutcomesAndBettingOffers, id);
            }

            if (entityType === ModelTypes.OUTCOME && filteredOutcomes.hasOwnProperty(id)) {
                filteredOutcomes = omit(filteredOutcomes, id);
            }

            if (entityType === ModelTypes.BETTING_OFFER && filteredBettingOffers.hasOwnProperty(id)) {
                filteredBettingOffers = omit(filteredBettingOffers, id);
            }

            if (entityType === ModelTypes.MARKET_OUTCOME_RELATION && filteredMarketAndOutcomeRelation.hasOwnProperty(id)) {
                filteredMarketAndOutcomeRelation = omit(filteredMarketAndOutcomeRelation, id);
            }
        });
    }

    if (updated > 0) {
        const { updates } = journal;

        updates.forEach((updatedElement: RecordUpdate<AggregatorOneOfTypeModel>) => {
            const { id, entityType, changedProperties } = updatedElement;

            if (entityType === ModelTypes.MATCH && filteredMatchesWithEvents.hasOwnProperty(id)) {
                filteredMatchesWithEvents = {
                    ...filteredMatchesWithEvents,
                    [id]: {
                        ...filteredMatchesWithEvents[id],
                        ...(changedProperties as Partial<AggregatorMatchModel>),
                    },
                };
            }

            if (entityType === ModelTypes.EVENT_INFO && filteredEventInfos.hasOwnProperty(id)) {
                filteredEventInfos = {
                    ...filteredEventInfos,
                    [id]: {
                        ...filteredEventInfos[id],
                        ...(changedProperties as Partial<AggregatorEventInfoModel>),
                    },
                };
            }

            if (entityType === ModelTypes.MAIN_MARKET) {
                filteredMainMarkets = {
                    ...filteredMainMarkets,
                    [id]: {
                        ...filteredMainMarkets[id],
                        ...changedProperties,
                    },
                };
            }

            if (entityType === ModelTypes.MARKET && marketsWithOutcomesAndBettingOffers.hasOwnProperty(id)) {
                marketsWithOutcomesAndBettingOffers = {
                    ...marketsWithOutcomesAndBettingOffers,
                    [id]: {
                        ...marketsWithOutcomesAndBettingOffers[id],
                        ...changedProperties,
                    },
                };
            }

            if (entityType === ModelTypes.OUTCOME && filteredOutcomes.hasOwnProperty(id)) {
                filteredOutcomes = {
                    ...filteredOutcomes,
                    [id]: {
                        ...filteredOutcomes[id],
                        ...(changedProperties as Partial<AggregatorOutcomeModel>),
                    },
                };
            }

            if (entityType === ModelTypes.BETTING_OFFER && filteredBettingOffers.hasOwnProperty(id)) {
                filteredBettingOffers = {
                    ...filteredBettingOffers,
                    [id]: {
                        ...filteredBettingOffers[id],
                        ...(changedProperties as Partial<AggregatorBettingOfferModel>),
                    },
                };
            }

            if (entityType === ModelTypes.BETTING_OFFER && filteredMarketAndOutcomeRelation.hasOwnProperty(id)) {
                filteredMarketAndOutcomeRelation = {
                    ...filteredMarketAndOutcomeRelation,
                    [id]: {
                        ...filteredMarketAndOutcomeRelation[id],
                        ...changedProperties,
                    },
                };
            }

            if (entityType === ModelTypes.SPORT && discipline) {
                discipline = {
                    ...discipline,
                    ...changedProperties as Partial<AggregatorDisciplineModel>,
                };
            }

        });

    }

    return {
        discipline: state.discipline,
        fetchedFromBrowser: isBrowser(),
        filteredMatchesWithEvents,
        filteredMatchesIds,
        filteredEventInfos,
        filteredMainMarketsList,
        filteredMainMarkets,
        filteredMarketGroupsOverview,
        filteredMarketGroupsOverviewList,
        marketsWithOutcomesAndBettingOffers,
        filteredOutcomes,
        filteredBettingOffers,
        filteredMarketAndOutcomeRelation,
        filteredStreamingProviderEventRelation,
        filteredEventStatistics,
        filteredMainOutrightsMarketsList,
    };
}

export function pickElementFromRecords(records: AggregatorOneOfTypeModel[], id: string, type: ModelTypes): AggregatorOneOfFilteredTypeModel {
    return remapEntries(records.find((item: AggregatorOneOfTypeModel) => {
        return item._type === type && item.id === id;
    })!);
}

